// UploadDropzone — v2-styled upload surface embedded in the landing's
// upload card (the card IS the source UX in the v2 design; the old
// UploadDrawer is gone). Supports file picker, folder picker, and
// drag-and-drop. Dispatches the same importUserFiles intent the drawer
// used — scenarioSaga parses, validates, caches, activates the user
// source, and lands the app in the portfolio ledger.

import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { importUserFiles } from '../../store/sagas/scenarioSaga.ts';
import { setError } from '../../store/slices/scenarioSourceSlice.ts';
import type { RootState } from '../../store/index.ts';
import type { ImportWarning } from '../../services/ScenarioImportService.ts';

export default function UploadDropzone() {
  const dispatch = useDispatch();
  const user = useSelector((s: RootState) => s.scenarioSource.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const processFiles = useCallback(
    (files: File[]) => {
      const jsonFiles = files.filter(
        (f) => f.type === 'application/json' || f.name.endsWith('.json'),
      );
      if (jsonFiles.length === 0) {
        dispatch(
          setError({
            source: 'user',
            error: 'No JSON files found. Please upload .json files only.',
          }),
        );
        return;
      }
      const warnings: ImportWarning[] = [];
      if (jsonFiles.length < files.length) {
        warnings.push({
          file: '',
          message: `${files.length - jsonFiles.length} non-JSON file(s) were ignored`,
        });
      }
      dispatch(importUserFiles({ files: jsonFiles, extraWarnings: warnings }));
    },
    [dispatch],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      if (e.target) e.target.value = '';
    },
    [processFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length === 0) return;
      processFiles(Array.from(e.dataTransfer.files));
    },
    [processFiles],
  );

  return (
    <div
      className={`fv2-dropzone${isDragOver ? ' fv2-dropzone-active' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
      }}
      onDrop={handleDrop}
      data-screen="upload-dropzone"
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".json,application/json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-label="Choose artifact files"
      />
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error -- webkitdirectory is non-standard; supported in Chrome, Edge, Safari, Firefox 50+
        webkitdirectory=""
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-label="Choose artifact folder"
      />

      <div className="fv2-dropzone-hint">
        {user.loading ? 'importing artifacts…' : 'drop files or a folder here, or'}
      </div>
      <div className="fv2-dropzone-actions">
        <button
          type="button"
          className="fv2-card-btn fv2-btn-accent"
          onClick={() => fileInputRef.current?.click()}
          disabled={user.loading}
        >
          CHOOSE FILES…
        </button>
        <button
          type="button"
          className="fv2-card-btn fv2-btn-outline"
          onClick={() => folderInputRef.current?.click()}
          disabled={user.loading}
        >
          FOLDER…
        </button>
      </div>

      {user.error && (
        <div className="fv2-card-error" role="alert">
          {user.error}
        </div>
      )}
      {user.warnings.length > 0 && (
        <div className="fv2-card-warnings">
          {user.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      )}
    </div>
  );
}
